
import { createFileRoute, Link } from '@tanstack/react-router'
import { AnnouncementGate } from '../../components/announcement-gate';
import { ClassCarousel } from '../../components/class-carousel';
import { ClassKindToggle } from '../../components/class-kind-toggle';
import { ClassSearchInput } from '../../components/class-search-input';
import { useClassKindFilter } from '../../lib/class-kind-filter';
import { useDebouncedValue } from '../../lib/use-debounced-value';
import { HeroCards } from '../../components/hero-cards';
import { ScheduledWidget } from '../../components/scheduled-widget';
import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import { FeedbackDialog, ConnectedTrendingPosts, HeroBanner, heroBannerChipClass, useUserType } from '@org/ui';
import { useAuth0 } from '@auth0/auth0-react';
import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { isImpersonating } from '../../lib/impersonation';

const COMMUNITY_APP_URL =
  import.meta.env.VITE_COMMUNITY_APP_URL ?? 'https://community.english2empower.com';

export const Route = createFileRoute('/_app/')({
  component: Index,
})

function Index() {
  const { t } = useTranslation('admin');
  const { user } = useAuth0();
  const { data: heroStats, isLoading: isHeroStatsLoading } = trpc.dashboard.heroStats.useQuery();
  // Viewing as a student is read-only: a survey could never be submitted or
  // dismissed, so neither popup opens (see lib/impersonation.ts).
  const [impersonating] = useState(isImpersonating);
  const { data: pendingCampaign } = trpc.feedback.pending.useQuery(undefined, { enabled: !impersonating });
  // Deduped with AnnouncementGate's query by react-query.
  const { data: pendingAnnouncements } = trpc.announcements.pending.useQuery(undefined, { enabled: !impersonating });
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackShownForId, setFeedbackShownForId] = useState<string | null>(null);
  const announcementsSeenThisSession = useRef(false);

  useEffect(() => {
    if ((pendingAnnouncements?.length ?? 0) > 0) announcementsSeenThisSession.current = true;
    // Precedence: announcements outrank feedback — while any announcement is (or was) pending this session, the feedback popup stays hidden.
    if (pendingAnnouncements === undefined || announcementsSeenThisSession.current) return;
    if (pendingCampaign && pendingCampaign.campaignId !== feedbackShownForId) {
      setFeedbackShownForId(pendingCampaign.campaignId);
      setFeedbackOpen(true);
    }
  }, [pendingCampaign, feedbackShownForId, pendingAnnouncements]);
  const utils = trpc.useUtils();

  const submitMutation = trpc.feedback.submit.useMutation({
    onSuccess: () => {
      utils.feedback.pending.invalidate();
      setFeedbackOpen(false);
      toast.success(t('p2e.feedback.dialog.successToast'));
    },
    onError: () => {
      toast.error(t('common.toasts.retryError', { action: 'submit feedback' }));
    },
  });

  const dismissMutation = trpc.feedback.dismiss.useMutation({
    onSuccess: () => {
      utils.feedback.pending.invalidate();
      setFeedbackOpen(false);
    },
  });
  const { isB2b, gender, isLoading: isUserTypeLoading } = useUserType();
  // "Aulas regulares | Todas as aulas" for the class carousel (remembered per device).
  const { kind: classKind, setKind: setClassKind, showToggle: showClassKindToggle } = useClassKindFilter();
  // The "lupa": keyword search over her classes, applied after a short pause in typing.
  const [classSearch, setClassSearch] = useState('');
  const debouncedClassSearch = useDebouncedValue(classSearch);
  // Feminine copy is the default; male students opt into the `_male` i18n
  // context variants (which fall back to the base key when absent).
  const genderContext = gender === 'male' ? 'male' : undefined;
  const heroChips = t('p2e.dashboard.hero.chips', { returnObjects: true }) as Array<{
    label: string;
    href: string;
  }>;
  // Onboard videos and "Central da Aluna" are woman-cohort destinations —
  // hidden for b2b, and while the cohort is unknown so they never flash in.
  const b2bHiddenRoutes = ['/onboard', '/central'];
  const visibleHeroChips = heroChips.filter(
    (chip) => !b2bHiddenRoutes.includes(chip.href) || (!isB2b && !isUserTypeLoading),
  );
  const groupLinks = heroStats?.groupLinks ?? null;
  const isHeroCardsLoading = isHeroStatsLoading && !heroStats;
  const firstName =
    user?.given_name ??
    user?.nickname ??
    t('p2e.dashboard.greeting.nameFallback', { context: genderContext });

  return (
    <div className="flex w-full flex-col gap-10 pb-16">
      <AnnouncementGate />
      <HeroBanner
        icon={
          <img
            aria-hidden="true"
            src="/icon-hand-small-white.png"
            alt=""
            className="h-12 w-12 object-contain"
          />
        }
        title={t('p2e.dashboard.greeting.line1', { name: firstName })}
        subtitle={t('p2e.dashboard.greeting.line2', { context: genderContext })}
        chips={
          <>
            {visibleHeroChips.map((chip) => (
              <Link key={chip.href} to={chip.href} className={heroBannerChipClass}>
                {chip.label}
              </Link>
            ))}
            {groupLinks?.recordingsFolderUrl && (
              <a
                href={groupLinks.recordingsFolderUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={heroBannerChipClass}
              >
                {t('p2e.dashboard.hero.dynamicChips.classRecordings')}
              </a>
            )}
            {groupLinks?.classNotesUrl && (
              <a
                href={groupLinks.classNotesUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={heroBannerChipClass}
              >
                {t('p2e.dashboard.hero.dynamicChips.classNotebook')}
              </a>
            )}
          </>
        }
      >
        <HeroCards
          heroStats={heroStats}
          isLoading={isHeroCardsLoading}
        />
      </HeroBanner>

      <div className="flex w-full flex-col gap-10 lg:flex-row lg:gap-6">
        <div className="min-w-0 flex-1 space-y-10">
          <section className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4 pb-2">
              <div>
                <p className="text-sm uppercase tracking-[0.3em] text-[#cf2e2e]">
                  {t('p2e.dashboard.classesSection.eyebrow')}
                </p>
                <h2 className="text-2xl font-semibold text-gray-900">{t('p2e.dashboard.classesSection.title')}</h2>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <ClassSearchInput value={classSearch} onChange={setClassSearch} />
                {showClassKindToggle && classKind && (
                  <ClassKindToggle value={classKind} onChange={setClassKind} />
                )}
              </div>
            </div>
            <div className="rounded-[32px] border border-rose-50 bg-white/90 p-4 shadow-sm lg:p-6">
              <ClassCarousel kind={classKind} search={debouncedClassSearch} />
            </div>
          </section>
        </div>

        <aside className="w-full shrink-0 lg:w-72" aria-label={t('p2e.dashboard.communityRail')}>
          <div className="space-y-4 lg:sticky lg:top-4">
            {/* Height caps keep the rail roughly level with the class carousel section. */}
            <ScheduledWidget seeAllHref="/calendar" className="lg:max-h-[28rem]" />
            <ConnectedTrendingPosts
              className="lg:max-h-[28rem]"
              limit={3}
              hrefBase={COMMUNITY_APP_URL}
              title={t('p2e.dashboard.fromCommunity')}
              emptyLabel={t('p2e.dashboard.noPosts')}
              emptyActionHref={COMMUNITY_APP_URL}
              emptyActionLabel={t('p2e.dashboard.visitCommunity')}
              viewAllHref={COMMUNITY_APP_URL}
              viewAllLabel={t('p2e.dashboard.visitCommunity')}
            />
          </div>
        </aside>
      </div>

      {pendingCampaign && (
        <FeedbackDialog
          open={feedbackOpen}
          onOpenChange={setFeedbackOpen}
          title={t('p2e.feedback.dialog.title')}
          questions={pendingCampaign.questions}
          campaignId={pendingCampaign.campaignId}
          eventId={pendingCampaign.eventId}
          isMandatory={pendingCampaign.isMandatory}
          onSubmit={(data) => submitMutation.mutate(data)}
          onDismiss={(data) => dismissMutation.mutate(data)}
          isSubmitting={submitMutation.isPending}
          labels={{
            submit: t('p2e.feedback.dialog.submit'),
            dismiss: t('p2e.feedback.dialog.dismiss'),
            submitting: t('common.status.submitting'),
          }}
        />
      )}
    </div>
  )
}
